import { getBusinessTravellerSelectEnabled } from 'businessTraveller/utils/getBusinessTravellerSelectEnabled'
import { getFrequentFlyerCheckoutFields } from 'businessTraveller/utils/getEmployeeFrequentFlyerCode'
import { getHotelLoyaltyCheckoutFields } from 'businessTraveller/utils/getEmployeeHotelLoyaltyCheckoutFields'
import { buildTravellerToRoomMap, getTravellerRoomIndex } from 'businessTraveller/utils/getTravellerRoomIndex'

export default function getLEBTTravellerForms({
  travellerEmployees,
  travellerGuestIds,
  selectedEmployeeDetails,
  account,
  currentRegionCode,
  businessAccount,
  currentTravellerForms,
  flightItems,
  accommodationItems,
  getOffer,
  isLEBTBookingForSelf,
}: {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  travellerGuestIds: Array<string>
  selectedEmployeeDetails: Array<App.BusinessTraveller.EmployeeDetails> | undefined
  account: App.AuthAccount
  currentRegionCode: string
  businessAccount: App.BusinessTraveller.CurrentBusinessAccount | undefined
  currentTravellerForms: Array<App.Checkout.TravellerForm>
  isGift: boolean,
  flightItems: Array<App.Checkout.FlightItem>,
  accommodationItems: Array<App.Checkout.LEHotelItem | App.Checkout.BedbankHotelItem>,
  getOffer: (offerId: string) => App.Offer | App.BedbankOffer | App.CruiseOffer | App.Tours.TourV2Offer | undefined,
  isLEBTBookingForSelf: boolean,
}): Array<App.Checkout.TravellerForm> {
  const isBusinessTravellerSelectEnabled = getBusinessTravellerSelectEnabled(businessAccount)
  const travellerToRoomMap = buildTravellerToRoomMap(accommodationItems)
  const bedbankItems = accommodationItems.filter(item => item.itemType === 'bedbankHotel')

  // Special case: When booking for self with no traveller employees selected,
  // create a form using the current business account employee
  if (isLEBTBookingForSelf && travellerEmployees.length === 0 && businessAccount?.status === 'success' && businessAccount.employee) {
    const employeeData = buildEmployeeFromAccount(account, businessAccount)
    const employeeForm: App.Checkout.TravellerForm = {
      id: '',
      type: 'employee',
      travellerOptionKey: `employee-${businessAccount.employee.id}`,
      employeeId: businessAccount.employee.id,
      roomIndex: getTravellerRoomIndex(businessAccount.employee.id, travellerToRoomMap),
      data: {
        ...employeeData,
        ...getFrequentFlyerCheckoutFields(businessAccount.employee, flightItems),
        ...(bedbankItems.length > 0 ? getHotelLoyaltyCheckoutFields(businessAccount.employee, bedbankItems, getOffer) : {}),
      },
    }
    return [employeeForm]
  }

  const employeeForms: Array<App.Checkout.TravellerForm> = travellerEmployees.map<App.Checkout.TravellerForm>((employee) => {
    // When booking for self, use currentBusinessAccount.employee data combined with auth account
    // When booking for others, use selectedEmployeeDetails from the API
    let employeeData
    if (isLEBTBookingForSelf && businessAccount?.status === 'success' && businessAccount.employee) {
      employeeData = buildEmployeeFromAccount(account, businessAccount)
    } else {
      employeeData = buildEmployeeFromDetails(selectedEmployeeDetails, employee)
    }

    return {
      id: '',
      type: 'employee',
      travellerOptionKey: `employee-${employee.id}`,
      employeeId: employee.id,
      roomIndex: getTravellerRoomIndex(employee.id, travellerToRoomMap),
      data: {
        ...employeeData,
        ...getFrequentFlyerCheckoutFields(employee, flightItems),
        ...(bedbankItems.length > 0 ? getHotelLoyaltyCheckoutFields(employee, bedbankItems, getOffer) : {}),
      },
    }
  })

  const guestForms: Array<App.Checkout.TravellerForm> = isBusinessTravellerSelectEnabled && travellerGuestIds.length > 0
    ? travellerGuestIds.map<App.Checkout.TravellerForm>((guestId) => {
        return {
          id: '',
          type: 'guest',
          travellerOptionKey: `guest-${guestId}`,
          guestId,
          roomIndex: getTravellerRoomIndex(guestId, travellerToRoomMap),
          data: {
            firstName: '',
            lastName: '',
            email: '',
            phonePrefix: '',
            phone: '',
            countryOfResidence: currentRegionCode,
            dateOfBirth: '',
            postcode: '',
            title: '',
          },
        }
      })
    : []

  const freshForms = [...employeeForms, ...guestForms]

  // Merge saved form data with fresh forms
  // This preserves user-entered data while updating employee details
  const mergedForms = freshForms.map((freshForm) => {
    let savedForm: App.Checkout.TravellerForm | undefined
    if (freshForm.type === 'employee' && freshForm.employeeId) {
      savedForm = currentTravellerForms.find(f =>
        f.type === 'employee' && f.employeeId === freshForm.employeeId
      )
    } else if (freshForm.type === 'guest' && freshForm.guestId) {
      savedForm = currentTravellerForms.find(f =>
        f.type === 'guest' && f.guestId === freshForm.guestId
      )
    }

    if (!savedForm) {
      return freshForm
    }

    // For employee forms, merge saved data with fresh employee details
    // This keeps user edits while ensuring employee data (like frequent flyer) is current
    if (freshForm.type === 'employee' && savedForm.type === 'employee' && freshForm.employeeId === savedForm.employeeId) {
      const employee = travellerEmployees.find(e => e.id === freshForm.employeeId)
      return {
        ...freshForm,
        id: savedForm.id,
        data: {
          ...freshForm.data,
          ...savedForm.data,
          // Always use fresh frequent flyer and loyalty data from API
          ...(employee ? getFrequentFlyerCheckoutFields(employee, flightItems) : {}),
          ...(bedbankItems.length > 0 && employee ? getHotelLoyaltyCheckoutFields(employee, bedbankItems, getOffer) : {}),
        },
      }
    }

    // For guest forms, keep saved data
    if (freshForm.type === 'guest' && savedForm.type === 'guest' && freshForm.guestId === savedForm.guestId) {
      return {
        ...freshForm,
        id: savedForm.id,
        data: {
          ...freshForm.data,
          ...savedForm.data,
        },
      }
    }

    return freshForm
  })

  return mergedForms
}

export function buildEmployeeFromDetails(selectedEmployeeDetails: Array<App.BusinessTraveller.EmployeeDetails> | undefined, employee: App.BusinessTraveller.EmployeeFromMeEndpoint) {
  // Activated employees have customerId – match by memberId/customerId.
  // Unactivated employees have no customerId – match by employeeId only (avoid null === null matching wrong employee).
  const employeeDetails = selectedEmployeeDetails?.find(selectedEmployee =>
    employee.customerId
      ? selectedEmployee.memberId === employee.customerId
      : selectedEmployee.employeeId === employee.id,
  )
  return {
    firstName: employeeDetails?.givenName ?? employee.firstName,
    lastName: employeeDetails?.surname ?? employee.lastName,
    email: employeeDetails?.email ?? employee.email,
    title: employeeDetails?.title ?? '',
    phonePrefix: employeeDetails?.phonePrefix?.replace(/^\++/, '') ?? '',
    phone: employeeDetails?.phone ?? '',
    countryOfResidence: employeeDetails?.countryCode ?? '',
    dateOfBirth: employeeDetails?.dob ?? '',
    postcode: employeeDetails?.postcode ?? '',
  }
}

function buildEmployeeFromAccount(account: App.AuthAccount, businessAccount: App.BusinessTraveller.CurrentBusinessAccount) {
  const currentEmployee = businessAccount.employee
  return {
    firstName: currentEmployee?.firstName,
    lastName: currentEmployee?.lastName,
    email: currentEmployee?.email,
    title: account.title ?? '',
    phonePrefix: account.phonePrefix?.replace(/^\++/, '') ?? '',
    phone: account.phone ?? '',
    countryOfResidence: account.countryCode ?? '',
    dateOfBirth: account.dob ?? '',
    postcode: account.postcode ?? '',
  }
}
